import { useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Package } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Capacitor } from '@capacitor/core';
import type { CardDeck } from '@shared/schema';
import { filterDecks } from '@/utils/deck-utils';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { MarketplaceFilters } from './marketplace-filters';
import { MarketplaceStats } from './marketplace-stats';
import { MarketplaceEmptyState } from './marketplace-empty-state';
import { MarketplaceGrid } from './marketplace-grid';
import { FeaturedDeck } from './featured-deck';

interface DeckMarketplaceContentProps {
  readonly showFeaturedDeck?: boolean;
  readonly onSelectDeck: (deckId: number) => void;
}

export function DeckMarketplaceContent({ 
  showFeaturedDeck = true,
  onSelectDeck 
}: Readonly<DeckMarketplaceContentProps>) {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("all");

  // Fetch decks from API
  const { data: decks, isLoading, isError } = useQuery<CardDeck[]>({
    queryKey: ["deck"],
    queryFn: async () => {
      let apiUrl = "/api/deck";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch decks");
      }
      return response.json();
    },
  });

  // Fetch user's owned decks
  const { data: userDecks } = useQuery({
    queryKey: ["user-decks"],
    queryFn: async () => {
      let apiUrl = "/api/deck/my-decks";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch user decks");
      return response.json();
    },
  });

  // Create a set of owned deck IDs for faster lookup
  const ownedDeckIds = new Set(userDecks?.map((userDeck: any) => userDeck.cardDeckId) || []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <RefreshCw className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading deck marketplace...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Package className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Failed to load decks. Please try again later.</p>
      </div>
    );
  }

  // Filter decks based on current filters
  const filteredDecks = filterDecks(decks, {
    searchQuery,
    selectedType,
    selectedFilter,
    activeTab
  });

  // Get available deck types for filtering
  const deckTypes = decks
    ? Array.from(new Set(decks.map((deck: CardDeck) => deck.deckType)))
    : [];
    
  // Get featured deck (premium deck or first deck)
  const featuredDeck = decks?.find(d => d.membership === "premium") ?? decks?.[0];

  // Reset filter handler
  const handleReset = () => {
    setSearchQuery("");
    setSelectedType("all");
    setSelectedFilter("all");
  };

  return (
    <div className="space-y-6">
      {showFeaturedDeck && featuredDeck && (
        <div className="mt-4">
          <FeaturedDeck 
            deck={featuredDeck} 
            onClick={() => onSelectDeck(featuredDeck.id)}
            isOwned={ownedDeckIds.has(featuredDeck.id)}
          />
        </div>
      )}

      <Tabs defaultValue="all" onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-black/40 border border-white/10">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              All Decks
            </TabsTrigger>
            <TabsTrigger 
              value="free" 
              className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              Free Members
            </TabsTrigger>
            <TabsTrigger 
              value="premium" 
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"
            >
              Premium
            </TabsTrigger>
          </TabsList>
        </div>

        <MarketplaceFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          viewMode={viewMode}
          setViewMode={setViewMode}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          deckTypes={deckTypes}
        />

        <MarketplaceStats
          filteredDecks={filteredDecks}
          selectedType={selectedType}
          searchQuery={searchQuery}
          allDecks={decks ?? []}
        />

        {['all', 'free', 'premium'].map((tabValue) => (
          <TabsContent key={tabValue} value={tabValue}>
            {filteredDecks.length === 0 ? (
              <MarketplaceEmptyState
                type={tabValue as 'all' | 'free' | 'premium'}
                searchQuery={searchQuery}
                onReset={handleReset}
              />
            ) : (
              <MarketplaceGrid
                decks={filteredDecks}
                viewMode={viewMode}
                onSelect={onSelectDeck}
                ownedDeckIds={ownedDeckIds}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}